import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { hrEmployerApi } from "@/lib/api/hr";
import { useHrInvalidate } from "@/hooks/use-hr-queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, Page, Panel, StatusBadge } from "@/components/shell/page";
import { todayIST } from "@/lib/timezone";

export default function EmployerTasksPage() {
  const invalidate = useHrInvalidate();
  const [day, setDay] = useState(todayIST());
  const [title, setTitle] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const assignments = useQuery({
    queryKey: ["hr", "employer", "assignments", day],
    queryFn: () => hrEmployerApi.taskAssignments({ day }),
  });
  const reports = useQuery({
    queryKey: ["hr", "employer", "reports", day],
    queryFn: () => hrEmployerApi.workReports(),
  });

  return (
    <Page
      title="Tasks"
      description="Assign work for a date, verify what was completed, and review daily reports."
      actions={
        <Input
          type="date"
          className="w-auto"
          aria-label="Task date"
          value={day}
          onChange={(e) => setDay(e.target.value)}
        />
      }
    >
      <Panel title="Add a task">
        <div className="grid gap-3 md:grid-cols-[10rem_1fr_auto] md:items-end">
          <div className="space-y-1">
            <Label htmlFor="task-employee">Employee ID</Label>
            <Input
              id="task-employee"
              placeholder="Numeric id"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="task-title">Title</Label>
            <Input id="task-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <Button
            onClick={async () => {
              await hrEmployerApi.upsertAssignment({
                employee_id: Number(employeeId),
                assignment_date: day,
                title,
              });
              setTitle("");
              invalidate();
            }}
          >
            Add task
          </Button>
        </div>
      </Panel>

      {(assignments.data ?? []).length === 0 && !assignments.isLoading && (
        <EmptyState title="No tasks on this date" body="Add one above, or pick another day." />
      )}
      <ul className="space-y-3">
        {(assignments.data ?? []).map((task: any) => (
          <li key={task.id} className="flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-medium">{task.title}</p>
              <p className="text-sm text-muted-foreground">Employee {task.employee_id}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={task.status} />
              {task.completion && !task.completion.verified && (
                <Button
                  size="sm"
                  onClick={async () => {
                    await hrEmployerApi.verifyAssignment(task.id);
                    invalidate();
                  }}
                >
                  Verify
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  await hrEmployerApi.upsertAssignment({
                    id: task.id,
                    employee_id: task.employee_id,
                    remove: true,
                  });
                  invalidate();
                }}
              >
                Remove
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <Panel title="Work reports">
        {(reports.data ?? []).length === 0 && (
          <p className="text-sm text-muted-foreground">No reports submitted yet.</p>
        )}
        <ul className="divide-y">
          {(reports.data ?? []).map((report: any) => (
            <li key={report.id} className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm">
                <p className="font-medium">Employee {report.employee_id}</p>
                <p className="text-muted-foreground">
                  {report.work_date} · {report.status}
                </p>
              </div>
              {report.status === "submitted" && (
                <Button
                  size="sm"
                  onClick={async () => {
                    await hrEmployerApi.reviewReport(report.id, "approved");
                    invalidate();
                  }}
                >
                  Approve
                </Button>
              )}
            </li>
          ))}
        </ul>
      </Panel>
    </Page>
  );
}
